import dayjs from 'dayjs'
import 'dayjs/locale/fi'
import relativeTime from 'dayjs/plugin/relativeTime'
import calendar from 'dayjs/plugin/calendar'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isoWeek from 'dayjs/plugin/isoWeek'

dayjs.extend(relativeTime)
dayjs.extend(calendar)
dayjs.extend(customParseFormat)
dayjs.extend(isoWeek)
dayjs.locale('fi')

const FINNISH_DATE_FORMATS = ['D.M.YYYY', 'DD.MM.YYYY', 'YYYY-MM-DD']

/**
 * Hand-typed Finnish date to the 'YYYY-MM-DD' Mantine works in. Null for a
 * fragment is the point: Mantine re-parses every keystroke and falls back to
 * `new Date(text)` when its own parse fails, and `new Date('2')` is 1.2.2001.
 */
export function parseFinnishDate(input: string): string | null {
  const parsed = dayjs(input.trim(), FINNISH_DATE_FORMATS, true)
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null
}

/**
 * Format a date with both date and time
 * Example: "26.8.2024 14:30"
 */
export function formatDateTime(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format a date without time
 * Example: "26.8.2024"
 */
export function formatDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format a date with abbreviated month
 * Example: "26. elo 2024, 14:30"
 */
export function formatDateTimeLong(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('fi-FI', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format relative time from now
 * Example: "2 tuntia sitten", "eilen", "3 päivää sitten"
 */
export function formatRelativeTime(date: string | Date): string {
  return dayjs(date).fromNow()
}

/**
 * Format a short date
 * Example: "26.8."
 */
export function formatShortDate(date: string | Date): string {
  const d = new Date(date)
  return d.toLocaleDateString('fi-FI', {
    day: 'numeric',
    month: 'numeric',
  })
}

/**
 * Check if a date is overdue (past current time)
 */
export function isOverdue(date: string | Date): boolean {
  return new Date(date) < new Date()
}

// Payments are imported into the system ~1 day after they're actually received,
// so an invoice paid on its due date isn't visible as paid until the next day.
// We grant that much grace before flagging an invoice as overdue, which
// guarantees an on-time payer is never shown as late. Single source of truth
// for both the JS badge logic (isOverdueAsOf) and the backend SQL cutoff (which
// builds on getOverdueCutoffDate). Raise this if the payment import cadence slows.
const OVERDUE_GRACE_DAYS = 1

/**
 * The grace-adjusted "overdue" cutoff as a 'YYYY-MM-DD' calendar day: an invoice
 * is overdue once its due date is strictly before this. `asOf` is the caller's
 * local calendar day. Computing the cutoff here (rather than as SQL date
 * arithmetic) lets the backend SQL fragments and the frontend badges share one
 * grace definition.
 */
export function getOverdueCutoffDate(asOf: string): string {
  return dayjs(asOf).subtract(OVERDUE_GRACE_DAYS, 'day').format('YYYY-MM-DD')
}

/**
 * Whether an invoice is overdue as of a given calendar day, applying the grace
 * period. `dueDate` and `asOf` are 'YYYY-MM-DD' calendar days; the comparison is
 * day-granular (no time-of-day), mirroring the backend SQL overdue cutoff
 * (modules/invoice/overdue.ts) so badges and list/KPI filters always agree.
 */
export function isOverdueAsOf({
  dueDate,
  asOf,
}: {
  dueDate: string
  asOf: string
}): boolean {
  return dayjs(dueDate).isBefore(dayjs(getOverdueCutoffDate(asOf)), 'day')
}

/**
 * Check if a date is today
 */
export function isToday(date: string | Date): boolean {
  const d = new Date(date)
  const today = new Date()
  return (
    d.getDate() === today.getDate() &&
    d.getMonth() === today.getMonth() &&
    d.getFullYear() === today.getFullYear()
  )
}

/**
 * Format time only
 * Example: "14:30"
 */
export function formatTime(date: string | Date): string {
  return dayjs(date).format('HH:mm')
}

/**
 * Convert a Date or string to ISO date string (YYYY-MM-DD)
 * Useful for sending date values to the API. With no argument, returns the
 * caller's local calendar day — the `asOf` reference for overdue calculations,
 * so the day boundary follows the user's timezone.
 * Example: new Date(2025, 0, 15) → "2025-01-15"
 */
export function toDateString(date?: string | Date): string {
  return dayjs(date).format('YYYY-MM-DD')
}

/** Monday of the ISO week containing the date (default: today), as 'YYYY-MM-DD'. */
export function toIsoWeekStart(date?: string | Date): string {
  return dayjs(date).startOf('isoWeek').format('YYYY-MM-DD')
}

/** True for a real 'YYYY-MM-DD' date that is a Monday. */
export function isIsoWeekStart(date: string): boolean {
  return (
    dayjs(date, 'YYYY-MM-DD', true).isValid() && toIsoWeekStart(date) === date
  )
}

/**
 * Inclusive day count between two dates (Mon→Mon = 1, Mon→Tue = 2). Floors at 1.
 */
export function dayCount(
  startDate: string | Date,
  endDate: string | Date,
): number {
  return Math.max(1, dayjs(endDate).diff(dayjs(startDate), 'day') + 1)
}

/**
 * Yield each calendar day from startDate to endDate inclusive as YYYY-MM-DD
 * strings. Order is ascending. If endDate is before startDate, yields nothing.
 */
export function* eachDayBetween(
  startDate: string | Date,
  endDate: string | Date,
): Generator<string> {
  const end = dayjs(endDate)
  let cursor = dayjs(startDate)
  while (!cursor.isAfter(end, 'day')) {
    yield cursor.format('YYYY-MM-DD')
    cursor = cursor.add(1, 'day')
  }
}

/**
 * Format duration between two dates
 * Example: "2h 30min" or "45min"
 * Returns null if duration is negative (invalid data)
 */
export function formatDuration(
  startDate: string | Date,
  endDate?: string | Date,
): string | null {
  const start = dayjs(startDate)
  const end = endDate ? dayjs(endDate) : dayjs()
  const diff = end.diff(start)

  if (diff < 0) return null

  const hours = Math.floor(diff / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))

  if (hours > 0) {
    return `${hours}h ${minutes}min`
  }
  return `${minutes}min`
}

/**
 * Format a date using calendar format with Finnish translations
 * Example: "Tänään", "Huomenna", "Eilen", "26.8."
 * @param date - Date to format
 * @param type - Whether the date is in the future (upcoming) or past
 */
export function formatCalendar(
  date: string | Date,
  type: 'upcoming' | 'past' = 'upcoming',
): string {
  const d = dayjs(date)

  if (type === 'upcoming') {
    return d.calendar(null, {
      sameDay: '[Tänään]',
      nextDay: '[Huomenna]',
      nextWeek: 'D.M.',
      sameElse: 'D.M.',
    })
  }

  return d.calendar(null, {
    sameDay: '[Tänään]',
    lastDay: '[Eilen]',
    lastWeek: 'D.M.',
    sameElse: 'D.M.',
  })
}
